"use no memo";

import { Status, TableShapes } from "@repo/database";
import { TableSchema } from "@repo/validators";
import { useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import type { Table } from "~/providers/table-provider";
import { useGeneral } from "~/hooks/use-general";
import { useSection } from "~/hooks/use-section";
import { useTable } from "~/hooks/use-table";
import { useTableManager } from "~/managers/use-table-manager";
import { useUnsavedChanges } from "~/providers/unsaved-changes-provider";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  useForm,
} from "../ui/form";
import { Input } from "../ui/input";
import { ModalInput } from "../ui/modal-input";
import { MultiSelect } from "../ui/multi-select";
import { Switch } from "../ui/switch";

const createPositionsWithEmptyValues = (tables: Table[], numEmpty: number) => {
  const initialPositions = tables
    .sort((a, b) => a.position - b.position)
    .map((table) => ({
      label: `${table.reference} (${table.position})`,
      value: table.position,
    }));

  const filledPositions = initialPositions.map((item) => item.value);
  const maxPosition = filledPositions[filledPositions.length - 1] ?? 0;

  // Generate empty positions between existing positions
  const emptyPositionsBetween = [];
  for (let i = 1; i <= maxPosition; i++) {
    if (!filledPositions.includes(i)) {
      emptyPositionsBetween.push({ label: `${i}`, value: i });
    }
  }

  // Generate additional empty positions after the last position
  const emptyPositionsAfter = Array.from({ length: numEmpty }, (_, i) => ({
    label: `${maxPosition + i + 1}`,
    value: maxPosition + i + 1,
  }));

  return [
    ...initialPositions,
    ...emptyPositionsBetween,
    ...emptyPositionsAfter,
  ];
};

export default function TableForm({
  table,
  onClose,
}: {
  table?: Table;
  onClose: (force?: boolean) => void;
}) {
  const { t } = useTranslation("common");

  const { sections } = useSection();

  const { prices } = useGeneral();

  const { tables } = useTable();

  const { createTable, updateTable } = useTableManager();

  const { setUnsavedChanges } = useUnsavedChanges();

  const form = useForm({
    reValidateMode: "onChange",
    schema: TableSchema,
    defaultValues: {
      status: table?.status ?? Status.active,
      minSeats: table?.minSeats ?? 2,
      maxSeats: table?.maxSeats ?? 2,
      bookable: table?.bookable ?? true,
      reference: table?.reference ?? "",
      position: table?.position ?? 1,
      locationId: table?.locationId ?? "",
      shape: table?.shape ?? undefined,
      priceId: table?.priceId ?? "",
    },
  });

  const selectedSection = useWatch({
    control: form.control,
    name: "locationId",
  });

  const positions = createPositionsWithEmptyValues(
    tables.filter((table) => table.locationId === selectedSection),
    10,
  ).sort((a, b) => a.value - b.value);

  const isDirty = !!Object.values(form.formState.dirtyFields).length;

  const priceOptions = [
    {
      value: "",
      label: "Default price",
    },
    ...prices.map((price) => ({
      label: price.name,
      value: price.id,
    })),
  ];

  setUnsavedChanges({
    isDirty,
    onDiscard() {
      form.reset();
      onClose(true);
    },
  });

  const handleSubmit = form.handleSubmit(async (formData) => {
    if (table) {
      const result = await updateTable({
        ...formData,
        id: table.id,
      });
      if (result === true) {
        form.reset();
        onClose(true);
      }
    } else {
      const { data, error } = await createTable(formData);
      if (data) {
        form.reset();
        onClose(true);
      } else if (error) {
        form.setError(error.path, { message: error.message });
      }
    }
  });

  return (
    <Form {...form}>
      <form
        id="table-form"
        onSubmit={handleSubmit}
        className="grid grid-cols-2 gap-2"
      >
        <FormField
          control={form.control}
          name="bookable"
          render={({ field }) => (
            <FormItem className="col-span-full flex items-center flex-row justify-between">
              <FormLabel>{t("Table.bookable")}</FormLabel>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="reference"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Table.reference")}</FormLabel>
              <FormControl>
                <Input {...field} placeholder={t("Table.reference")} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="minSeats"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Table.min_seats")}</FormLabel>
              <FormControl>
                <ModalInput
                  min={0}
                  max={25}
                  label={t("Table.min_seats")}
                  type="number"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="maxSeats"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Table.max_seats")}</FormLabel>
              <FormControl>
                <ModalInput
                  min={0}
                  max={25}
                  label={t("Table.max_seats")}
                  type="number"
                  {...field}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="locationId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Table.section")}</FormLabel>
              <FormControl>
                <MultiSelect
                  maxSelect={1}
                  options={sections
                    .sort((a, b) => a.position - b.position)
                    .map((section) => ({
                      label: section.name,
                      value: section.id,
                    }))}
                  value={[field.value]}
                  defaultValue={field.value ? [field.value] : []}
                  onValueChange={(value) => {
                    field.onChange(value[0]);
                  }}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="position"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Table.position")}</FormLabel>
              <MultiSelect
                maxSelect={1}
                placeholder="Select position..."
                onValueChange={(value) => {
                  field.onChange(value[0]);
                  void form.trigger();
                }}
                options={positions}
                defaultValue={[field.value]}
                value={[field.value]}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="shape"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Table.shape")}</FormLabel>
              <MultiSelect
                maxSelect={1}
                placeholder="Select print route..."
                onValueChange={(value) => {
                  field.onChange(value[0]);
                }}
                options={Object.values(TableShapes).map((shape) => ({
                  label: shape,
                  value: shape,
                }))}
                defaultValue={field.value ? [field.value] : []}
                value={field.value}
              />
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="priceId"
          render={({ field }) => (
            <FormItem className="col-span-full">
              <FormLabel>{t("Table.price")}</FormLabel>
              <MultiSelect
                maxSelect={1}
                placeholder="Select a price..."
                onValueChange={(value) => {
                  field.onChange(value[0]);
                }}
                options={priceOptions}
                defaultValue={[field.value]}
                value={[field.value]}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  );
}
